import { ref } from 'vue';

import useSsoPromptState from '@/composables/useSsoPromptState';
import { lookupSsoByEmail } from '@/utils/ssoEmailLookup';

/**
 * Shared state and logic for prompting users with an SSO-enabled email domain
 * to sign in with SSO instead of a password, used by both the login and
 * registration forms.
 */
export default function useSsoEmailPrompt() {
  const {
    ssoEmailMatch,
    isPromptVisible: showSsoPrompt,
    resetPrompt,
  } = useSsoPromptState();
  const passwordLoginEmail = ref<string | null>(null);

  /**
   * Looks up the email's SSO configuration and shows the prompt when it
   * matches. Returns `true` when the prompt was shown and the caller should
   * stop, or `false` when the caller should proceed with password auth. The
   * lookup is skipped (returning `false`) once the user has chosen password
   * auth for this email, so retries go straight to password auth.
   */
  async function maybePromptForSso(email: string): Promise<boolean> {
    if (email === passwordLoginEmail.value) return false;
    try {
      const match = await lookupSsoByEmail(email);
      if (match) {
        ssoEmailMatch.value = match;
        showSsoPrompt.value = true;
        return true;
      }
    } catch {
      // Fall back to password auth if the SSO lookup fails unexpectedly.
    }
    return false;
  }

  function dismissForPassword(email: string) {
    resetPrompt();
    passwordLoginEmail.value = email;
  }

  return {
    showSsoPrompt,
    ssoEmailMatch,
    passwordLoginEmail,
    maybePromptForSso,
    dismissForPassword,
  };
}
